import {
  BOULEVARD_DAYS_AHEAD,
  BOULEVARD_FLAG_LABELS,
  type BoulevardConnectionView,
  type BoulevardFlagView,
  type BoulevardMapKind,
  type BoulevardMappingRow,
  type BoulevardMappingView,
  type BoulevardVisitSummary,
  formatCents,
} from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Field, Loading, Panel, PanelHeader, formatDateTime } from "../../components/ui";

/**
 * Settings → Boulevard, appointments: match Boulevard's staff, services, and locations to Vaulted's
 * ("Map these"), sync appointments from a date, and settle what the sync flagged.
 */
export function BoulevardAppointments({ connection }: { connection: BoulevardConnectionView }) {
  const qc = useQueryClient();
  const mapping = useQuery({ queryKey: ["boulevard", "mapping"], queryFn: () => get<BoulevardMappingView>("/api/boulevard/mapping") });
  const setMapping = (v: BoulevardMappingView) => qc.setQueryData(["boulevard", "mapping"], v);
  const read = useMutation({ mutationFn: () => post<BoulevardMappingView>("/api/boulevard/mapping/read", {}), onSuccess: setMapping });
  const map = useMutation({
    mutationFn: (v: { kind: BoulevardMapKind; boulevardId: string; vaultedId: string | null; skipped: boolean }) => put<BoulevardMappingView>("/api/boulevard/mapping", v),
    onSuccess: setMapping,
  });
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const sync = useMutation({
    mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/sync/appointments", { from }),
    onSuccess: (v) => qc.setQueryData(["boulevard"], v),
  });
  const receipts = useMutation({
    mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/sync/receipts", {}),
    onSuccess: (v) => qc.setQueryData(["boulevard"], v),
  });
  const running = connection.runs.some((r) => r.status === "running");
  const rc = connection.receipts;

  if (mapping.isLoading) return <Loading />;
  if (mapping.error || !mapping.data) return <Alert>{errorMessage(mapping.error)}</Alert>;
  const m = mapping.data;
  const open = (rows: BoulevardMappingRow[]) => rows.filter((r) => !r.vaultedId && !r.skipped).length;
  const toMatch = open(m.locations) + open(m.staff) + open(m.services);

  return (
    <>
      <Panel>
        <PanelHeader
          title="Appointments: map these first"
          description="Match Boulevard's locations, staff, and services to Vaulted's (same names are matched for you). An appointment using one that isn't matched waits; one marked Skip is left out of the visit."
          action={
            <Button busy={read.isPending} onClick={() => read.mutate()}>
              {m.readAt ? "Read Boulevard's lists again" : "Read Boulevard's lists"}
            </Button>
          }
        />
        <div className="flex flex-col gap-4 px-5 py-4 text-sm">
          {read.error && <Alert>{errorMessage(read.error)}</Alert>}
          {map.error && <Alert>{errorMessage(map.error)}</Alert>}
          {m.readAt ? (
            <p className="text-ink-soft">
              Read {formatDateTime(m.readAt)}. {toMatch ? <span className="font-semibold text-wait">{toMatch} to match.</span> : "Everything is matched or skipped."}
            </p>
          ) : (
            <p className="text-muted">Not read yet.</p>
          )}
          <MapList title="Locations" kind="location" rows={m.locations} options={m.options.locations} onPick={(v) => map.mutate(v)} />
          <MapList title="Staff" kind="staff" rows={m.staff} options={m.options.staff} onPick={(v) => map.mutate(v)} />
          <MapList title="Services and add-ons" kind="service" rows={m.services} options={m.options.services.map((s) => ({ id: s.id, name: `${s.category ? `${s.category} › ` : ""}${s.name}${s.type === "add_on" ? " (add-on)" : ""}` }))} onPick={(v) => map.mutate(v)} />
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Sync appointments"
          description={`Brings Boulevard's appointments in as visits, from the date you pick through ${BOULEVARD_DAYS_AHEAD} days ahead. Each is linked by Boulevard's appointment ID, so syncing again (even from an earlier date) never makes a second one. Nothing is texted or emailed.`}
        />
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Button variant="primary" busy={sync.isPending} disabled={running || !from || !m.locations.some((l) => l.vaultedId)} onClick={() => sync.mutate()}>
              Sync appointments
            </Button>
          </div>
          {sync.error && <Alert>{errorMessage(sync.error)}</Alert>}
          <p className="text-xs text-muted">
            Until a visit starts here, Boulevard owns its time, nurse, and services (change or cancel it there). Once it's marked arrived, it's Vaulted's: chart and check out as usual, and anything Boulevard changes after that is flagged below instead of copied.
          </p>
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Receipts"
          description="Each visit's checkout as Boulevard recorded it: every line's final price and discount, the tip, and how it was paid (card, voucher, gift card). Visits finished in Boulevard get theirs as they sync; imported past visits get theirs here. Only the money is read, nothing about the client."
        />
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          {rc.linkFound === false ? (
            <Alert>Boulevard didn't say how an appointment points to its receipt, so receipts can't be read yet. Tell Claude; it needs one more request from Boulevard's appointment screen.</Alert>
          ) : rc.linkFound === null ? (
            <p className="text-muted">Read Boulevard's lists (above) first: that also finds how to read receipts.</p>
          ) : null}
          <p className="text-ink-soft">
            {rc.saved} receipts read. {rc.pastVisitsWithout ? `${rc.pastVisitsWithout} imported past visits don't have theirs yet (about a second each).` : "Every imported past visit has its receipt (or has none in Boulevard)."}
          </p>
          <div>
            <Button busy={receipts.isPending} disabled={running || rc.linkFound !== true || !rc.pastVisitsWithout} onClick={() => receipts.mutate()}>
              Get receipts for past visits
            </Button>
          </div>
          {receipts.error && <Alert>{errorMessage(receipts.error)}</Alert>}
        </div>
      </Panel>

      <TakeOver connection={connection} running={running} />

      <Flags count={connection.openFlags} />
    </>
  );
}

/** The cutover: upcoming visits synced from Boulevard become Vaulted's. Once, and not undone. */
function TakeOver({ connection, running }: { connection: BoulevardConnectionView; running: boolean }) {
  const qc = useQueryClient();
  const t = connection.takeover;
  const [result, setResult] = useState<{ visits: number; chairs: number; noChair: number; noCard: number } | null>(null);
  const go = useMutation({
    mutationFn: () => post<BoulevardConnectionView & { takenOver: { visits: number; chairs: number; noChair: number; noCard: number } }>("/api/boulevard/take-over", {}),
    onSuccess: ({ takenOver, ...v }) => (qc.setQueryData(["boulevard"], v), setResult(takenOver)),
  });
  const fresh = !!t.lastSyncAt && Date.now() - Date.parse(t.lastSyncAt) < 30 * 60_000;
  return (
    <Panel>
      <PanelHeader
        title="Take over Boulevard visits"
        description="At cutover: every upcoming visit synced from Boulevard becomes Vaulted's. You can change or cancel it here, and its reminders and texts come from Vaulted (none are sent for the takeover itself, and none for visits in the next 24 hours, which Boulevard already reminded). Each clinic visit gets a chair. Appointments stop syncing after this. Turn off online booking in Boulevard at the same time."
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {t.at ? (
          <p className="text-ink-soft">Taken over {formatDateTime(t.at)}.{t.upcoming ? ` ${t.upcoming} upcoming visits synced since are still Boulevard's.` : ""}</p>
        ) : (
          <>
            <p className="text-ink-soft">
              {t.upcoming} upcoming visits to take over. Appointments last synced {t.lastSyncAt ? formatDateTime(t.lastSyncAt) : "never"}
              {fresh ? "." : ": sync appointments first (it has to be within 30 minutes), so nothing changed in Boulevard is missed."}
            </p>
            <div>
              <Button
                variant="danger"
                busy={go.isPending}
                disabled={running || !fresh || !t.upcoming}
                onClick={() => window.confirm(`Take over ${t.upcoming} upcoming visits from Boulevard? They become Vaulted's and appointments stop syncing. This can't be undone.`) && go.mutate()}
              >
                Take over {t.upcoming} visits
              </Button>
            </div>
          </>
        )}
        {result && (
          <Alert tone="ok">
            Took over {result.visits} visits; {result.chairs} given a chair.
            {result.noChair ? ` ${result.noChair} couldn't get a chair` : ""}
            {result.noChair && result.noCard ? " and" : ""}
            {result.noCard ? ` ${result.noCard} have no card on file` : ""}
            {result.noChair || result.noCard ? ": they're under Check these below." : ""}
          </Alert>
        )}
        {go.error && <Alert>{errorMessage(go.error)}</Alert>}
      </div>
    </Panel>
  );
}

function MapList({
  title,
  kind,
  rows,
  options,
  onPick,
}: {
  title: string;
  kind: BoulevardMapKind;
  rows: BoulevardMappingRow[];
  options: { id: string; name: string }[];
  onPick: (v: { kind: BoulevardMapKind; boulevardId: string; vaultedId: string | null; skipped: boolean }) => void;
}) {
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const waiting = rows.filter((r) => !r.vaultedId && !r.skipped);
  const shown = all ? rows : waiting;
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-3">
        <h3 className="font-semibold">{title}</h3>
        <span className="text-muted">
          {rows.length - waiting.length} of {rows.length} matched or skipped
        </span>
        <button type="button" className="text-brand hover:underline" onClick={() => setAll(!all)}>
          {all ? "Show only unmatched" : "Show all"}
        </button>
      </div>
      {shown.length ? (
        <ul className="divide-y divide-mist rounded border border-line">
          {shown.map((r) => (
            <li key={r.boulevardId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span>{r.label}</span>
              <select
                aria-label={`Vaulted match for ${r.label}`}
                className="rounded border border-line bg-white px-2 py-1"
                value={r.skipped ? "skip" : (r.vaultedId ?? "")}
                onChange={(e) => onPick({ kind, boulevardId: r.boulevardId, vaultedId: e.target.value && e.target.value !== "skip" ? e.target.value : null, skipped: e.target.value === "skip" })}
              >
                <option value="">Not matched yet</option>
                <option value="skip">Skip (leave it out)</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted">All matched.</p>
      )}
    </div>
  );
}

const line = (s: BoulevardVisitSummary | undefined) =>
  s ? [s.date, s.time, s.staff, s.services.join(", "), s.totalCents != null ? formatCents(s.totalCents) : null, s.status].filter(Boolean).join(" · ") : "—";

/** What the sync flagged: each shows both sides; settling it means someone checked (and fixed what needed fixing). */
function Flags({ count }: { count: number }) {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ["boulevard", "flags", count], queryFn: () => get<BoulevardFlagView[]>("/api/boulevard/flags") });
  const settle = useMutation({
    mutationFn: (id: string) => post(`/api/boulevard/flags/${id}/settle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["boulevard"] }),
  });
  return (
    <Panel>
      <PanelHeader
        title="Check these"
        description="Visits the sync brought in but wants a person to look at. Nothing here was changed or thrown away: open the visit, fix what needs fixing, then mark it checked."
        action={count ? <Badge tone="wait">{count} to check</Badge> : <Badge tone="ok">Nothing to check</Badge>}
      />
      {settle.error && (
        <div className="px-5 pt-3">
          <Alert>{errorMessage(settle.error)}</Alert>
        </div>
      )}
      {flags.isLoading ? (
        <Loading />
      ) : flags.data?.length ? (
        <ul className="divide-y divide-mist px-5 text-sm">
          {flags.data.map((f) => (
            <li key={f.id} className="flex flex-col gap-1 py-3">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-semibold">{BOULEVARD_FLAG_LABELS[f.kind]}</span>
                <Link to={`/clients/${f.clientId}`} className="text-brand hover:underline">
                  {f.clientName}
                </Link>
                <span className="text-muted">{formatDateTime(f.start)}</span>
              </div>
              {f.details.boulevard && (
                <p>
                  <span className="text-muted">Boulevard: </span>
                  {line(f.details.boulevard)}
                </p>
              )}
              {f.details.other && (
                <p>
                  <span className="text-muted">{f.details.otherLabel ?? "Other"}: </span>
                  {line(f.details.other)}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Link to={`/visits/${f.appointmentId}`} className="text-brand hover:underline">
                  Open visit
                </Link>
                {f.details.otherAppointmentId && (
                  <Link to={`/visits/${f.details.otherAppointmentId}`} className="text-brand hover:underline">
                    Open the other visit
                  </Link>
                )}
                <Button variant="quiet" busy={settle.isPending && settle.variables === f.id} onClick={() => settle.mutate(f.id)}>
                  Checked
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-3 text-sm text-muted">Nothing to check.</p>
      )}
    </Panel>
  );
}
